import { NextPageContext } from 'next/dist/shared/lib/utils'
import { GetServerSidePropsContext, PreviewData } from 'next/types'
import nookies, { setCookie } from 'nookies'
import { ParsedUrlQuery } from 'querystring'

import { User } from '@/generated/api/@types'
import { pagesPath } from '@/generated/lib/$path'
import { EUserPermission } from '@/src/const/userPermission'
import { ADMINISTRATOR, CALL_CENTER_STAFF } from '@/src/const/userRole'

import { cookieExpires } from '../config/clientApi'
import { authorizedPagesMasterExceptStaff, unAuthorizedPages } from '../const/path'

export const SIGN_IN_PATH = pagesPath.account.sign_in.$url().pathname
export const UNAUTHORIZED_ACCESS_MESSAGE = 'アクセス不可です。ホームページに遷移します。'

const getPathname = (url: string | undefined): string => {
  if (!url) return ''
  return url.split('?')[0].split('#')[0]
}

export const resWriteHead = (
  ctx: NextPageContext | GetServerSidePropsContext<ParsedUrlQuery, PreviewData>,
  status: number,
  location: string
) => {
  const { res, req } = ctx
  if (!res || !req) return

  const currentPathname = getPathname(req.url)
  const targetPathname = getPathname(location)
  if (currentPathname === targetPathname) return
  res.writeHead(status, {
    location,
  })
  res.end()
}

/**
 * サインイン画面のパスを返す。taskInfo があればサインイン後にその業務画面へ戻れるように引き継ぐ。
 */
export const signInPathWithTaskInfo = (taskInfo?: string | string[]) =>
  taskInfo ? `${SIGN_IN_PATH}?taskInfo=${taskInfo}` : SIGN_IN_PATH

/**
 * サーバーサイド (Amplify) でのログイン確認。
 *
 * API への到達性は ALB の IP 制限で利用者の固定 IP に限られており、Amplify のサーバーからは
 * API を呼べない。そのためここでは Cookie にトークンがあるかだけを確認し、
 * トークンの有効性と画面ごとの権限はブラウザ側の MwAuthGuard で確認する。
 */
export const isAuthenticated = (ctx: NextPageContext) => {
  const { req } = ctx
  if (!req?.url) return
  const pathname = getPathname(req.url)
  if (unAuthorizedPages.includes(pathname)) return

  const cookies = nookies.get(ctx)
  const taskInfo = ctx.query?.taskInfo
  if (!cookies?.token) {
    resWriteHead(ctx, 302, signInPathWithTaskInfo(taskInfo))
    return
  }

  // 有効期限を延長する
  setCookie(ctx, 'token', cookies.token, cookieExpires)

  if (taskInfo) {
    resWriteHead(ctx, 302, `/my/tasks/${taskInfo}`)
  }
}

/**
 * ログインユーザーが指定のページ (router.pathname 形式) を表示できるか。
 */
export const isPathAllowedForUser = (pathname: string, user: User): boolean => {
  if (unAuthorizedPages.includes(pathname)) return true

  const role = user.user_role

  // スタッフ編集権限のみあるuserのみ/master/users系許可
  if (
    pathname.startsWith(pagesPath.master.users.$url().pathname) &&
    !(
      role.edit_admin_users ||
      role.edit_call_center_users ||
      role.edit_partner_users ||
      role.edit_phone_line_admin_users ||
      role.edit_phone_line_users
    )
  ) {
    return false
  }

  // マスタ管理配下のスタッフ以外の項目系はedit_tasks=1のみ許可
  if (
    authorizedPagesMasterExceptStaff.includes(pathname) &&
    role.edit_tasks !== EUserPermission.READ_WRITE
  ) {
    return false
  }

  // 葬儀系はedit_funeralsの権限に応じて、画面アクセス可能。
  if (pathname.startsWith(pagesPath.my.funerals.$url().pathname) && !role.edit_funerals) {
    return false
  }

  // 業務edit_tasksの権限に応じて、画面アクセス可能
  if (pathname.startsWith(pagesPath.my.tasks.$url().pathname) && !role.edit_tasks) {
    return false
  }

  if (
    pathname.startsWith(pagesPath.my.tasks.pending_list.$url().pathname) &&
    role.id !== ADMINISTRATOR &&
    role.id !== CALL_CENTER_STAFF
  ) {
    return false
  }

  return true
}

/**
 * @deprecated 画面ごとの権限確認はブラウザ側の MwAuthGuard が行う。
 * 各ページの getServerSideProps からの呼び出し互換のために残している (何もしない)。
 */
export const isAuthorizedAccess = (_ctx: NextPageContext | GetServerSidePropsContext) => undefined
